import { z } from 'zod'
import { IsoDateTimeSchema } from '../primitives'
import { route } from '../route-def'
import { RegistrationStatusSchema } from './registration'

/**
 * Own-capture path (spec P1 "Own-capture path is provider-generic", E8.2–E8.6, E11.2–E11.4; R42; AC2, AC3, AC23,
 * AC32). Every `flow: 'own_capture'` provider uses these two endpoints: the page asks for a capture token (which
 * carries the provider's frame plan and, when the plan has one, the server-chosen challenge direction), runs the
 * shared camera screen, and uploads exactly `profile.frames` frames with the token (multipart, never JSON).
 */

/** Server-chosen challenge direction (apps/api `CHALLENGE_DIRECTIONS`; profile.ts fails to compile if they drift). */
export const CaptureChallengeSchema = z.enum(['up', 'down', 'left', 'right'])
export type CaptureChallenge = z.infer<typeof CaptureChallengeSchema>

/**
 * The frame plan of an own-capture provider (apps/api `CaptureProfile`). 'timed': frames at the given offsets once
 * the face is in place; 'motion': frame 1 when the face is in place, frame 2 when the motion trigger fires.
 */
export const CaptureProfileSchema = z
  .object({
    frames: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    secondFrame: z.enum(['timed', 'motion']),
    timingsMs: z.array(z.number().int().min(0).max(10_000)).optional(),
    challenge: z.enum(['none', 'direction']),
    motionTimeoutMs: z.number().int().min(1000).max(30_000).optional(),
    motionThresholds: z.object({ desktop: z.number().min(1).max(100), mobile: z.number().min(1).max(100) }).optional(),
    encoding: z.enum(['jpeg', 'png']),
    mirror: z.boolean(),
  })
  .refine((p) => p.secondFrame !== 'timed' || p.timingsMs?.length === p.frames, {
    message: "a 'timed' profile needs one timing per frame",
    path: ['timingsMs'],
  })
  .refine((p) => p.secondFrame !== 'motion' || (p.frames >= 2 && p.motionTimeoutMs !== undefined), {
    message: "a 'motion' profile needs at least two frames and motionTimeoutMs",
    path: ['motionTimeoutMs'],
  })
export type CaptureProfileContract = z.infer<typeof CaptureProfileSchema>

export const CaptureTokenBodySchema = z.object({
  /** The S4 biometric consent box (separate, unticked by default; it names the provider). */
  consentBiometric: z.literal(true),
})
export type CaptureTokenBody = z.infer<typeof CaptureTokenBodySchema>

export const CaptureTokenResponseSchema = z.object({
  /** Single use, valid 60 s, bound to this registration (or member session for an R32 retry). */
  captureToken: z.string(),
  expiresAt: IsoDateTimeSchema,
  profile: CaptureProfileSchema,
  /** The direction the page asks for (challenge profiles only); the upload never carries it back. */
  challenge: CaptureChallengeSchema.nullable(),
})
export type CaptureTokenResponse = z.infer<typeof CaptureTokenResponseSchema>

/**
 * Why an upload was refused (422 `capture_refused`, `details.reason`). A refused upload is not an attempt and spends
 * no provider credit; the page asks the member to try again.
 */
export const CAPTURE_REFUSAL_REASONS = [
  'token_unknown',
  'token_used',
  'token_other_owner',
  'token_expired',
  'capture_too_fast',
  'capture_rate_limited',
  /** The registration's provider changed after the token was issued. */
  'provider_changed',
  /** Not exactly `profile.frames` frames. */
  'frame_count',
  /** A frame is neither JPEG nor PNG (sniffed on the bytes). */
  'mime',
  /** A frame is wider or taller than 4096 px (read from its header, never decoded). */
  'dimensions',
  /** A frame's header states no width and height (no JPEG SOFn / PNG IHDR): refused before any decode. */
  'frame_unreadable',
  /** The E3 gate refused the template frame (or the motion frame): `details.qualityReasons`. */
  'quality',
  /** The frames do not show the same face. */
  'consistency',
  /** T23 (E12.5): the provider could not match the post-liveness still to the live scan; take another (not a try). */
  'still_not_matched',
] as const
export const CaptureRefusalReasonSchema = z.enum(CAPTURE_REFUSAL_REASONS)
export type CaptureRefusalReason = z.infer<typeof CaptureRefusalReasonSchema>

export const captureRoutes = {
  captureToken: route({
    method: 'POST',
    path: '/api/member/capture/token',
    auth: 'member_or_registration',
    body: CaptureTokenBodySchema,
    response: CaptureTokenResponseSchema,
  }),
  /** multipart/form-data: field `token` and files `frames` (exactly `profile.frames`); answers the registration. */
  captureUpload: route({
    method: 'POST',
    path: '/api/member/capture',
    auth: 'member_or_registration',
    response: RegistrationStatusSchema,
  }),
}
